/**
 * Camera Motion Detector
 * Detects camera stability to prevent false marker triggers during motion
 */

const DEFAULT_STABILITY_THRESHOLD = 0.005; // Position change threshold (unitless)
const DEFAULT_STABILITY_DURATION = 300; // Required stable duration in ms
const DEFAULT_HISTORY_SIZE = 10; // Number of frames to track

/**
 * Detects camera motion and stability for AR marker selection
 */
export class CameraMotionDetector {
  constructor(options = {}) {
    this.stabilityThreshold = options.stabilityThreshold || DEFAULT_STABILITY_THRESHOLD;
    this.stabilityDuration = options.stabilityDuration || DEFAULT_STABILITY_DURATION;
    this.historySize = options.historySize || DEFAULT_HISTORY_SIZE;

    this.positionHistory = [];
    this.isStable = false;
    this.stableSince = null;
    this.lastVelocity = 0;

    console.log(
      `🎥 CameraMotionDetector initialized: threshold=${this.stabilityThreshold}, duration=${this.stabilityDuration}ms`
    );
  }

  /**
   * Update motion detector with current camera position
   * @param {THREE.Camera} camera - Three.js camera object
   * @returns {boolean} - True if camera is stable
   */
  update(camera) {
    const now = performance.now();
    const currentPosition = camera.position.clone();

    // Add to history
    this.positionHistory.push({
      position: currentPosition,
      timestamp: now,
    });

    // Limit history size
    if (this.positionHistory.length > this.historySize) {
      this.positionHistory.shift();
    }

    // Need at least 2 frames to calculate velocity
    if (this.positionHistory.length < 2) {
      this.isStable = false;
      this.stableSince = null;
      return false;
    }

    // Calculate velocity (average over recent frames)
    const velocity = this._calculateVelocity();
    this.lastVelocity = velocity;

    // Check if velocity is below threshold
    const isBelowThreshold = velocity < this.stabilityThreshold;

    if (isBelowThreshold) {
      // Camera is moving slowly
      if (!this.stableSince) {
        // Just became stable
        this.stableSince = now;
        this.isStable = false;
        console.log(`📍 Camera stabilizing (v=${velocity.toFixed(6)})`);
        return false;
      } else {
        // Check if stable for required duration
        const stableDuration = now - this.stableSince;
        if (stableDuration >= this.stabilityDuration) {
          if (!this.isStable) {
            console.log(
              `✅ Camera stable for ${stableDuration.toFixed(0)}ms (v=${velocity.toFixed(6)})`
            );
          }
          this.isStable = true;
          return true;
        } else {
          // Still stabilizing
          this.isStable = false;
          return false;
        }
      }
    } else {
      // Camera is moving too fast
      if (this.isStable) {
        console.log(
          `🚫 Camera motion detected (v=${velocity.toFixed(6)} > ${this.stabilityThreshold})`
        );
      }
      this.isStable = false;
      this.stableSince = null;
      return false;
    }
  }

  /**
   * Calculate average velocity over recent frames
   * @returns {number} - Average velocity magnitude
   */
  _calculateVelocity() {
    if (this.positionHistory.length < 2) {
      return 0;
    }

    let totalVelocity = 0;
    let count = 0;

    for (let i = 1; i < this.positionHistory.length; i++) {
      const prev = this.positionHistory[i - 1];
      const curr = this.positionHistory[i];

      const deltaTime = (curr.timestamp - prev.timestamp) / 1000; // Convert to seconds
      if (deltaTime === 0) continue;

      const distance = curr.position.distanceTo(prev.position);
      const velocity = distance / deltaTime;

      totalVelocity += velocity;
      count++;
    }

    return count > 0 ? totalVelocity / count : 0;
  }

  /**
   * Reset detector state
   */
  reset() {
    this.positionHistory = [];
    this.isStable = false;
    this.stableSince = null;
    this.lastVelocity = 0;
    console.log('🔄 CameraMotionDetector reset');
  }

  /**
   * Get current detector state
   * @returns {Object} - Current state information
   */
  getState() {
    const now = performance.now();
    return {
      isStable: this.isStable,
      stableDuration: this.stableSince ? now - this.stableSince : 0,
      velocity: this.lastVelocity,
      threshold: this.stabilityThreshold,
      requiredDuration: this.stabilityDuration,
      historyLength: this.positionHistory.length,
    };
  }

  /**
   * Update stability parameters dynamically
   * @param {number} threshold - New stability threshold
   * @param {number} duration - New stability duration (ms)
   */
  updateParameters(threshold, duration) {
    this.stabilityThreshold = threshold;
    this.stabilityDuration = duration;
    console.log(
      `🎥 CameraMotionDetector parameters updated: threshold=${threshold}, duration=${duration}ms`
    );
  }
}
